import { relevancyWeight } from "../../api/domain.ts";
import type { Persona, ReportSnapshot, RelevancyRating } from "../../api/domain.ts";
import { relevancySteps } from "../../components/Form.tsx";
import type { CompareLevel } from "./compare.ts";

/**
 * The comparison read through one persona's weighting.
 *
 * The scores on the wheel stay the global ones: what a product is worth is a
 * fact about the product. What the lens adds is how much each axis counts for
 * this persona, and what the product totals become once that is applied. Both
 * come straight from the snapshot: the ratings from its input, the weighted
 * totals from the engine, so nothing here invents a number the report would
 * not print.
 */
export type PersonaLens = {
  persona: string;
  name: string;
  /** Whether the persona has rated anything; without ratings the lens has nothing to show. */
  rated: boolean;
  /** The persona's own total for a product, or null where the engine has none. */
  weightedScore: (product: string) => number | null;
  /** The weight this persona gives one axis, on 0 to 1 with 0.5 the neutral step. */
  weightOf: (level: CompareLevel, axis: string) => number | null;
};

/** The neutral step's weight: `=`, and what an unrated capability counts as. */
export const neutralWeight = relevancyWeight("equal");

/**
 * The personas a customer user reads the comparison as: the ones their customer
 * owns. Empty for a consultant, who picks a persona explicitly instead.
 */
export const ownPersonas = (snapshot: ReportSnapshot, customer: string | undefined): Persona[] =>
  customer === undefined
    ? []
    : snapshot.input.personas.filter((persona) => persona.customer === customer);

/** The personas the snapshot was evaluated for, in input order. */
export const lensCandidates = (snapshot: ReportSnapshot): Persona[] =>
  snapshot.input.personas.filter((persona) =>
    snapshot.evaluation.personas.some((entry) => entry.persona === persona.id),
  );

/**
 * The lens for one persona, or undefined where the snapshot does not know it.
 *
 * A topic's weight mirrors `topic_factor` in the core: the mean rating weight
 * over the topic's capabilities. The core takes the mean over the capabilities
 * a product has a score for, which differs per product; the lens takes every
 * capability of the topic, because one mark has to stand for both sides of the
 * wheel. Where nobody rated a capability it counts as `=`.
 */
export const personaLens = (snapshot: ReportSnapshot, persona: string): PersonaLens | undefined => {
  const named = snapshot.input.personas.find((entry) => entry.id === persona);
  const evaluated = snapshot.evaluation.personas.find((entry) => entry.persona === persona);
  if (named === undefined || evaluated === undefined) return undefined;

  const ratings = new Map<string, RelevancyRating>();
  for (const relevancy of snapshot.input.relevancies ?? []) {
    if (relevancy.persona === persona && relevancy.active) {
      ratings.set(relevancy.capability, relevancy.rating);
    }
  }

  const capabilityWeight = (capability: string): number => {
    const rating = ratings.get(capability);
    return rating === undefined ? neutralWeight : relevancyWeight(rating);
  };

  return {
    persona,
    name: named.name,
    rated: ratings.size > 0,
    weightedScore: (product) =>
      evaluated.products.find((entry) => entry.product === product)?.weighted_score ?? null,
    weightOf: (level, axis) => {
      if (level === "capability") return capabilityWeight(axis);
      const members = snapshot.input.capabilities.filter((capability) => capability.topic === axis);
      if (members.length === 0) return null;
      return members.reduce((sum, member) => sum + capabilityWeight(member.id), 0) / members.length;
    },
  };
};

/**
 * How a weight scales a distance: 1 at the neutral step, 2 at `++`, 0 at `--`.
 * This is the same factor the core applies to a topic's coefficient.
 */
export const weightFactor = (weight: number): number => weight / neutralWeight;

/** The rating step nearest a weight, for drawing a topic's mean on the five-step scale. */
export const nearestRating = (weight: number): RelevancyRating =>
  relevancySteps.reduce((best, step) =>
    Math.abs(relevancyWeight(step.rating) - weight) <
    Math.abs(relevancyWeight(best.rating) - weight)
      ? step
      : best,
  ).rating;

/** The tone class name for a weight, so a mark and a mean share one colour scale. */
export const toneOf = (weight: number): string =>
  relevancySteps.find((step) => step.rating === nearestRating(weight))?.tone ?? "equal";

/** Which way the persona's total moved a product against the global score. */
export type Shift = "up" | "down" | "level";

export const shiftOf = (global: number | null, weighted: number | null): Shift => {
  if (global === null || weighted === null) return "level";
  const delta = Math.round((weighted - global) * 100);
  return delta > 0 ? "up" : delta < 0 ? "down" : "level";
};
